import React, { memo } from 'react';
import { Card } from '../components/UI/Card';
import { Link } from 'react-router-dom';
export const Blog = () => {
  interface BlogPost {
    id: number;
    title: string;
    excerpt: string;
    content: React.ReactNode;
    author: string;
    date: string;
    image: string;
    slug: string;
  }
  const blogPosts: BlogPost[] = [{
    id: 1,
    title: 'The Art & Science of Relationship Building',
    excerpt: 'When I first started in human capital development, I used to walk into networking events with one mission: meet as many people as possible.',
    content: <div className="space-y-4" data-id="element-1084">
          <p data-id="element-1085">
            When I first started in human capital development, I used to walk
            into networking events with one mission: 👉 meet as many people as
            possible.
          </p>
          <p data-id="element-1086">
            I'd leave with a stack of business cards and feel like I was
            winning. But a week later, most of those "connections" barely
            remembered who I was.
          </p>
          <p data-id="element-1087">Then one day, something clicked.</p>
          <p data-id="element-1088">
            At an event in Abuja, I stopped trying to sell myself and instead
            spent the evening asking people about their stories, their
            challenges, what excited them.
          </p>
          <p data-id="element-1089">
            One person I spoke with that night became a long-term partner — not
            because I pitched, but because I listened.
          </p>
          <p data-id="element-1090">That experience taught me the real formula:</p>
          <h3 className="text-xl font-bold mt-6 mb-2" data-id="element-1091">🔬 The Science</h3>
          <ul className="list-disc pl-6 space-y-2" data-id="element-1092">
            <li data-id="element-1093">
              People are wired to respond to genuine interest (psychology calls
              it reciprocity).
            </li>
            <li data-id="element-1094">
              Familiarity and consistency breed trust — it's why check-ins
              matter more than cold pitches.
            </li>
            <li data-id="element-1095">
              Relationships are built on emotional memory, not transactional
              exchanges.
            </li>
          </ul>
          <h3 className="text-xl font-bold mt-6 mb-2" data-id="element-1096">🎨 The Art</h3>
          <p data-id="element-1097">What I've learned: it's the small, human gestures that matter.</p>
          <ul className="list-disc pl-6 space-y-2" data-id="element-1098">
            <li data-id="element-1099">✅ Remembering a client's daughter just started school.</li>
            <li data-id="element-1100">✅ Sending a voice note instead of a generic text.</li>
            <li data-id="element-1101">✅ Following up months later — without an agenda.</li>
          </ul>
          <p data-id="element-1102">
            Those little moments have led to opportunities I could never have
            "strategized" into existence.
          </p>
          <h3 className="text-xl font-bold mt-6 mb-2" data-id="element-1103">💡 Takeaway</h3>
          <p data-id="element-1104">
            Relationships aren't built at once. They're built brick by brick,
            through curiosity, empathy, and consistency.
          </p>
          <p data-id="element-1105">
            And in a world obsessed with growth hacks and shortcuts, the deepest
            relationships remain the ultimate unfair advantage.
          </p>
          <p className="mt-6 font-medium" data-id="element-1106">
            👉 What's one moment in your career when a simple, human gesture
            turned into a lasting relationship?
          </p>
        </div>,
    author: 'Andrew Igbineweka',
    date: 'August 15, 2025',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/62DL8VzUWoZSsys88qcCmf/speaking_anne.jpg',
    slug: 'art-science-relationship-building'
  }, {
    id: 2,
    title: 'How to Make the Most of Your First Networking Event',
    excerpt: 'Attending your first networking event can be intimidating. Here are five strategies to help you navigate it successfully.',
    content: <div className="space-y-4" data-id="element-1107">
          <p data-id="element-1108">
            Walking into a room full of strangers can be daunting, especially
            when you're expected to make meaningful connections. But with the
            right mindset and approach, your first networking event can be the
            beginning of valuable professional relationships.
          </p>
          <h3 className="text-xl font-bold mt-6 mb-2" data-id="element-1109">
            1. Set Clear Intentions
          </h3>
          <p data-id="element-1110">
            Before the event, decide what you want to achieve. Is it meeting
            three new people in your industry? Learning about a specific topic?
            Having a conversation with a particular speaker? Having clear goals
            helps focus your energy and measure success beyond collecting
            business cards.
          </p>
          <h3 className="text-xl font-bold mt-6 mb-2" data-id="element-1111">
            2. Prepare Your Introduction
          </h3>
          <p data-id="element-1112">
            Craft a concise, engaging introduction that conveys who you are and
            what you're passionate about. Practice until it feels natural, not
            rehearsed. Remember, the goal is conversation, not perfection.
          </p>
          <h3 className="text-xl font-bold mt-6 mb-2" data-id="element-1113">
            3. Ask Thoughtful Questions
          </h3>
          <p data-id="element-1114">
            Great networkers are great listeners. Prepare open-ended questions
            that show genuine interest in others. "What are you working on that
            excites you right now?" often yields more interesting conversations
            than "What do you do?"
          </p>
          <h3 className="text-xl font-bold mt-6 mb-2" data-id="element-1115">
            4. Find the Connectors
          </h3>
          <p data-id="element-1116">
            In every room, there are people who know everyone. These connectors
            can introduce you to others who align with your interests or goals.
            How to spot them? They're often greeting multiple people or being
            approached frequently.
          </p>
          <h3 className="text-xl font-bold mt-6 mb-2" data-id="element-1117">
            5. Follow Up Meaningfully
          </h3>
          <p data-id="element-1118">
            Within 48 hours, reach out to people you connected with. Reference
            something specific from your conversation and suggest a clear next
            step if appropriate. A thoughtful follow-up is what transforms a
            brief meeting into a potential relationship.
          </p>
          <p className="mt-6 font-medium" data-id="element-1119">
            Remember, meaningful networking isn't about quantity—it's about
            quality. One authentic connection can be more valuable than a dozen
            surface-level interactions.
          </p>
        </div>,
    author: 'The Networking Salon Team',
    date: 'July 22, 2025',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/gumJfCcrUgAhRz8naofitq/Network_tobi.jpg',
    slug: 'make-most-first-networking-event'
  }, {
    id: 3,
    title: 'Building Community in a Digital-First World',
    excerpt: 'As remote work becomes the norm, how do we maintain authentic human connections?',
    content: <div className="space-y-4" data-id="element-1120">
          <p data-id="element-1121">
            The shift to digital-first work environments has fundamentally
            changed how we build and maintain professional relationships. While
            technology has removed geographical barriers, it has created new
            challenges for fostering genuine community.
          </p>
          <h3 className="text-xl font-bold mt-6 mb-2" data-id="element-1122">The Digital Paradox</h3>
          <p data-id="element-1123">
            We're more connected than ever through digital tools, yet many
            professionals report feeling increasingly isolated. This paradox
            exists because while information exchange has become easier,
            emotional connection requires more intentional effort in virtual
            spaces.
          </p>
          <h3 className="text-xl font-bold mt-6 mb-2" data-id="element-1124">
            Creating Digital Rituals
          </h3>
          <p data-id="element-1125">
            Strong communities are built on shared rituals. In a digital
            environment, these might include:
          </p>
          <ul className="list-disc pl-6 space-y-2" data-id="element-1126">
            <li data-id="element-1127">
              Weekly virtual coffee chats with no agenda beyond connection
            </li>
            <li data-id="element-1128">
              Monthly skill-sharing sessions where team members teach something
              they're passionate about
            </li>
            <li data-id="element-1129">
              Quarterly virtual retreats focused on relationship-building
              alongside strategic planning
            </li>
          </ul>
          <h3 className="text-xl font-bold mt-6 mb-2" data-id="element-1130">
            The Power of Vulnerability
          </h3>
          <p data-id="element-1131">
            Digital spaces can feel impersonal, making vulnerability even more
            powerful. Leaders who share challenges, ask for help, and
            acknowledge uncertainty create psychological safety that allows
            communities to deepen.
          </p>
          <h3 className="text-xl font-bold mt-6 mb-2" data-id="element-1132">
            Hybrid Connection Models
          </h3>
          <p data-id="element-1133">
            The most resilient communities blend digital and in-person
            experiences. Consider:
          </p>
          <ul className="list-disc pl-6 space-y-2" data-id="element-1134">
            <li data-id="element-1135">
              Using digital tools for regular touchpoints and information
              sharing
            </li>
            <li data-id="element-1136">
              Reserving in-person gatherings for deeper relationship building
              and complex collaboration
            </li>
            <li data-id="element-1137">
              Creating "digital campfires" – smaller, more intimate online
              gatherings that foster closer connections
            </li>
          </ul>
          <p className="mt-6 font-medium" data-id="element-1138">
            The future belongs to organizations and leaders who can thoughtfully
            design community experiences that leverage both digital convenience
            and human connection. The goal isn't to replicate in-person
            experiences online, but to create new models of community that
            embrace the unique advantages of both worlds.
          </p>
        </div>,
    author: 'The Networking Salon Team',
    date: 'June 5, 2025',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/9ciCFjhcNKhhvWPuNF4wxL/Network_Scoot.jpg',
    slug: 'building-community-digital-first-world'
  }];
  return <div className="pt-20" data-id="element-1139">
      <section className="py-16 bg-secondary/5" data-id="element-1140">
        <div className="container mx-auto px-4" data-id="element-1141">
          <h1 className="text-4xl font-bold text-center text-secondary-dark mb-6" data-id="element-1142">
            Blog
          </h1>
          <p className="text-center text-lg text-secondary max-w-3xl mx-auto" data-id="element-1143">
            Insights, stories, and practical advice on networking, community
            building, and professional growth.
          </p>
        </div>
      </section>
      <section className="py-16" data-id="element-1144">
        <div className="container mx-auto px-4" data-id="element-1145">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-1146">
            {blogPosts.map(post => <Link to={`/blog/${post.slug}`} key={post.id} className="block" data-id="element-1147">
                <Card className="flex flex-col cursor-pointer hover:shadow-md transition-shadow h-full" data-id="element-1148">
                  <div className="aspect-[16/9] relative overflow-hidden rounded-t-lg" data-id="element-1149">
                    <img src={post.image} alt={post.title} className="w-full h-full object-cover" data-id="element-1150" />
                  </div>
                  <div className="p-6 flex-grow flex flex-col" data-id="element-1151">
                    <h2 className="text-xl font-bold text-secondary-dark mb-2" data-id="element-1152">
                      {post.title}
                    </h2>
                    <p className="text-secondary mb-4 flex-grow" data-id="element-1153">
                      {post.excerpt}
                    </p>
                    <div className="flex items-center justify-between mt-auto pt-4 border-t border-gray-200" data-id="element-1154">
                      <span className="text-sm text-secondary" data-id="element-1155">
                        {post.author}
                      </span>
                      <span className="text-sm text-secondary" data-id="element-1156">
                        {post.date}
                      </span>
                    </div>
                  </div>
                </Card>
              </Link>)}
          </div>
        </div>
      </section>
    </div>;
};